import React, { useState } from "react";

const Tooltip = (props) => {
  const timeout = React.useRef(null);
  const [active, setActive] = useState(false);
  const [position, setPosition] = useState({
    left: 0,
    top: 0,
    arrowLeft: "50%",
    arrowTop: "50%",
    isAdjusted: false,
  });

  const updatePosition = React.useCallback(() => {
    if (!active) return;
    const newPosition = getTooltipPosition(props.target, props.direction);
    setPosition(newPosition);
  }, [props.target, props.direction, active]);

  React.useEffect(() => {
    const targetElement = document.getElementById(props.target);

    if (!targetElement) {
      console.warn(
        `Tooltip target element with id "${props.target}" not found`
      );
      return;
    }

    const tooltipPosition = getTooltipPosition(props.target, props.direction);
    setPosition(tooltipPosition);

    const handleMouseEnter = () => {
      timeout.current = setTimeout(() => {
        const tooltipPosition = getTooltipPosition(
          props.target,
          props.direction
        );
        setPosition(tooltipPosition);
        // Only set active after position is calculated
        requestAnimationFrame(() => {
          setActive(true);
        });
      }, props.delay || 0);
    };

    const handleMouseLeave = () => {
      clearTimeout(timeout.current);
      setActive(false);
    };

    targetElement.addEventListener("mouseenter", handleMouseEnter);
    targetElement.addEventListener("mouseleave", handleMouseLeave);
    targetElement.addEventListener("focusin", handleMouseEnter);
    targetElement.addEventListener("focusout", handleMouseLeave);
    window.addEventListener("scroll", updatePosition);
    window.addEventListener("resize", updatePosition);

    return () => {
      targetElement.removeEventListener("mouseenter", handleMouseEnter);
      targetElement.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
      clearTimeout(timeout.current);
    };
  }, [props.target, props.delay, props.direction, updatePosition]);

  return (
    <div
      className="tooltip-wrapper"
      style={{ pointerEvents: "none" }}
      data-target={props.target}
    >
      <div
        id={props.id}
        className={`tooltip-tip ${props.direction || "top"} ${
          active ? "active" : ""
        } ${position.isAdjusted ? "adjusted" : ""}`}
        style={{
          position: "fixed",
          left: position.left,
          top: position.top,
          "--arrow-left": position.arrowLeft,
          "--arrow-top": position.arrowTop,
        }}
        role="tooltip"
      >
        {props.children}
      </div>
    </div>
  );
};

const getTooltipPosition = (targetId, direction = "top") => {
  const targetElement = document.getElementById(targetId);
  if (!targetElement) return {};

  const rect = targetElement.getBoundingClientRect();
  const tooltipElement = document.querySelector(
    `[data-target="${targetId}"] .tooltip-tip`
  );
  if (!tooltipElement) return {};

  const tooltipRect = tooltipElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const safetyMargin = 20; // 1.25rem

  // Add max-width calculation based on available space
  const availableSpace = getSpaceBetweenTargetAndViewport(targetId, direction);
  const shouldLimitWidth = availableSpace < 300 + safetyMargin * 2;
  if (shouldLimitWidth) {
    tooltipElement.classList.add("limited-width");
    tooltipElement.style.maxWidth = `${availableSpace - safetyMargin * 2}px`;
  } else {
    tooltipElement.classList.remove("limited-width");
    tooltipElement.style.maxWidth = "";
  }

  let positions = {
    top: {
      left: rect.left + rect.width / 2 - tooltipRect.width / 2,
      top: rect.top - 10,
      arrowLeft: "50%",
    },
    bottom: {
      left: rect.left + rect.width / 2 - tooltipRect.width / 2,
      top: rect.bottom + 10,
      arrowLeft: "50%",
    },
    left: {
      left: rect.left - 10,
      top: rect.top + rect.height / 2,
      arrowTop: "50%",
    },
    right: {
      left: rect.right + 10,
      top: rect.top + rect.height / 2,
      arrowTop: "50%",
    },
  };

  let isAdjusted = false;

  // Adjust horizontal position if tooltip goes outside viewport
  if (
    positions[direction].left + tooltipRect.width >
    viewportWidth - safetyMargin
  ) {
    const newLeft = viewportWidth - tooltipRect.width - safetyMargin;
    if (["top", "bottom"].includes(direction)) {
      positions[direction].arrowLeft = `${
        rect.left + rect.width / 2 - newLeft
      }px`;
    }
    positions[direction].left = newLeft;
    isAdjusted = true;
  }
  if (positions[direction].left < safetyMargin) {
    const newLeft = safetyMargin;
    if (["top", "bottom"].includes(direction)) {
      positions[direction].arrowLeft = `${
        rect.left + rect.width / 2 - newLeft
      }px`;
    }
    positions[direction].left = newLeft;
    isAdjusted = true;
  }

  // Adjust vertical position if tooltip goes outside viewport
  if (
    positions[direction].top + tooltipRect.height >
    viewportHeight - safetyMargin
  ) {
    const newTop = viewportHeight - tooltipRect.height - safetyMargin;
    if (["left", "right"].includes(direction)) {
      positions[direction].arrowTop = `${
        rect.top + rect.height / 2 - newTop
      }px`;
    }
    positions[direction].top = newTop;
    isAdjusted = true;
  }
  if (positions[direction].top < safetyMargin) {
    const newTop = safetyMargin;
    if (["left", "right"].includes(direction)) {
      positions[direction].arrowTop = `${
        rect.top + rect.height / 2 - newTop
      }px`;
    }
    positions[direction].top = newTop;
    isAdjusted = true;
  }

  return {
    ...positions[direction],
    isAdjusted,
  };
};

const getSpaceBetweenTargetAndViewport = (targetPosition, direction) => {
  const targetElement = document.getElementById(targetPosition);
  const targetRect = targetElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  let space = 0;
  switch (direction) {
    case "left":
      space = targetRect.left;
      break;
    case "right":
      space = viewportWidth - targetRect.right;
      break;
    default:
      space = 0;
      break;
  }

  return space;
};

export default Tooltip;
